import { Dispatch, SetStateAction } from 'react'
import { Switch, Disclosure, Transition } from '@headlessui/react'
import { SimpleArrowIcon, FireMembership } from 'assets/icons'
import { MEMBERSHIP_BENEFITS_LIST } from 'constants/membership'
import { MEMBERSHIP_STATUS } from 'constants/subscriptions'
import SuspendedMembership from 'components/SuspendedMembership'
import WORDING from 'constants/wording'
import { toLocaleCurrency } from 'utils/dinero'
import RoundedIcon from 'components/RoundedIcon'
import { MEMBERSHIP_PRICE } from 'config'

const { SUSPENDED_MEMBERSHIP_ADVANCE } = WORDING

const { ACTIVE, SUSPENDED, DELAYED } = MEMBERSHIP_STATUS

interface MembershipOptionsProps {
  suscriptionIsMandatoryError: boolean
  isMembershipSelected: boolean
  setIsMembershipSelected: Dispatch<SetStateAction<boolean>>
  membershipStatus: keyof typeof MEMBERSHIP_STATUS
  isInterestFree?: boolean
}

const MembershipOptions = ({
  suscriptionIsMandatoryError,
  isMembershipSelected,
  setIsMembershipSelected,
  membershipStatus,
  isInterestFree = false
}: MembershipOptionsProps) => {
  if (membershipStatus === ACTIVE) return null
  if (membershipStatus === DELAYED && !isInterestFree) return null

  if (membershipStatus === SUSPENDED)
    return (
      <SuspendedMembership
        bigIcon
        className='border-2 border-red-900'
        paragraphText={SUSPENDED_MEMBERSHIP_ADVANCE}
        eventName='bille_boton_adelantos_membresia_suspendida'
      />
    )

  if (membershipStatus === DELAYED)
    return (
      <SuspendedMembership
        bigIcon
        title='Tenés una cuota demorada de tu membresía.'
        className='border-2 border-red-900'
        paragraphText='Pagá tu deuda, volvé y pedí tu adelanto a tasa 0'
        eventName='bille_boton_adelantos_membresia_demorada'
      />
    )

  return (
    <div
      className={`rounded-bl-md w-full bg-white transition-all duration-100 rounded-lg border border-gray-500 p-3 ${
        suscriptionIsMandatoryError && 'border-2 border-red-900'
      } `}
    >
      <div className='flex justify-between items-center'>
        <RoundedIcon
          className='!h-9 !w-9 mr-2'
          icon={<FireMembership className='w-[14px]' />}
        />
        <div className='flex-grow'>
          <p className='text-black font-bold text-xs'>
            Membresía{' '}
            <span className='font-bold'>
              Wayni<span className='text-green-800'>+</span>
            </span>{' '}
            por $ {toLocaleCurrency(MEMBERSHIP_PRICE)}
          </p>
          <p className='text-gray-900 text-2xs font-medium'>
            Suscribite para obtener tu adelanto.
          </p>
        </div>
        <Switch
          className='transition'
          checked={isMembershipSelected}
          onChange={setIsMembershipSelected}
        >
          {({ checked }) => (
            <div
              className={`${checked ? 'bg-green-800' : 'bg-gray-500'}  ${
                suscriptionIsMandatoryError &&
                'ring-1 ring-red-900 bg-[#FEE0D9]'
              } inline-flex h-7 w-14 items-center rounded-full`}
            >
              <span className='sr-only'>Habilitar membresía</span>
              <span
                className={`${
                  checked ? 'translate-x-8 sm:translate-x-13' : 'translate-x-1'
                } inline-block h-5 w-5 transform rounded-full bg-white transition-all`}
              />
            </div>
          )}
        </Switch>
      </div>
      <hr className='my-2 border' />
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className='w-full justify-between flex pt-1 items-center'>
              <p className='leading-tight text-purple-900 font-bold text-xs'>
                Conocé los beneficios
              </p>
              <div className='shrink-0 flex'>
                <span
                  className={`rounded-full transition-all duration-200 flex items-center justify-center border border-gray-300 h-6 w-6 ${
                    !!open && 'bg-green-800 border-none'
                  }`}
                >
                  <SimpleArrowIcon
                    className={`transition-all transform duration-200 ${
                      open ? 'transform -rotate-90' : 'transform rotate-90'
                    }`}
                    stroke={open ? 'white' : '#442E83'}
                  />
                </span>
              </div>
            </Disclosure.Button>
            <Transition
              enter='transition duration-100 ease-out'
              enterFrom='transform scale-95 opacity-0'
              enterTo='transform scale-100 opacity-100'
              leave='transition duration-100 ease-out'
              leaveFrom='transform scale-100 opacity-100'
              leaveTo='transform scale-95 opacity-0'
            >
              <Disclosure.Panel className='bg-white rounded-lg'>
                {MEMBERSHIP_BENEFITS_LIST.map((item, idx) => (
                  <li
                    key={idx}
                    className='my-3 w-full flex items-start justify-start list-none'
                  >
                    <img
                      className='shrink-0 w-3 mt-[5px]'
                      src={item.img}
                      alt={item.alt}
                    />
                    <div className='w-full ml-2'>
                      <h6 className='text-black font-bold text-xs'>
                        {item.title}
                      </h6>
                      <p className='text-black font-normal text-2xs'>
                        {item.description}
                      </p>
                      {item.clarification && (
                        <p className='text-[#7C7C7C] text-2xs'>
                          {item.clarification}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
    </div>
  )
}

export default MembershipOptions
